import {
  AnalyticsApi,
  AnalyticsEvent,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/**
 * Maps raw Backstage event actions to namespaced backend values.
 * 'create' is excluded here — scaffold_create is handled separately below
 * via a deduplication window that merges the create + navigate signals.
 */
const ACTION_MAP: Record<string, string> = {
  navigate: 'entity_navigate',
  search: 'entity_search',
};

/** Matches the scaffolder task page URL (e.g. /create/tasks/<uuid>). */
const SCAFFOLDER_TASK_RE = /\/create\/tasks\/([0-9a-f-]{36})/;

/**
 * Analytics listener that forwards Backstage navigate / create / search events
 * to the custom-metrics backend. All other event types are silently ignored.
 * Any network or HTTP error is caught and swallowed so the UI is never broken.
 */
export class CustomMetricsAnalytics implements AnalyticsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  /**
   * Deduplication state for scaffold_create.
   *
   * The Backstage scaffolder emits two signals when a template is submitted:
   *  1. A 'create' analytics event (carries entityRef, but may be delayed or
   *     dropped in React 18 concurrent mode before the component unmounts).
   *  2. A navigate event to /create/tasks/<uuid> (always fires, no entityRef).
   *
   * We schedule a single scaffold_create POST on the first signal and upgrade
   * the entityRef if the second signal arrives within the 1-second window.
   * This prevents double-counting while preserving the template identity.
   */
  private scaffoldPending: {
    timer: ReturnType<typeof setTimeout>;
    entityRef: string | null;
    event: AnalyticsEvent;
  } | null = null;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  captureEvent(event: AnalyticsEvent): void {
    // ── scaffold_create dedup window ──────────────────────────────────────────
    if (event.action === 'create') {
      this.scheduleScaffoldCreate(
        event,
        (event.context.entityRef as string | undefined) ?? null,
      );
      return; // handled; do not fall through to ACTION_MAP
    }

    if (event.action === 'navigate' && SCAFFOLDER_TASK_RE.test(event.subject)) {
      // Fire as navigate too (entity_navigate via ACTION_MAP below),
      // but also schedule scaffold_create with null entityRef as fallback.
      this.scheduleScaffoldCreate(event, null);
    }

    // ── standard action mapping ───────────────────────────────────────────────
    const namespacedAction = ACTION_MAP[event.action];
    if (!namespacedAction) {
      return;
    }

    // Fire-and-forget: errors must never propagate to the UI.
    this.postEvent(namespacedAction, event).catch(() => {
      // intentionally swallowed
    });
  }

  /**
   * Schedules a single scaffold_create POST within a 1-second dedup window.
   * If called twice (once from 'create', once from navigate), only one POST
   * is made. The entityRef from whichever call has it takes priority.
   */
  private scheduleScaffoldCreate(
    event: AnalyticsEvent,
    entityRef: string | null,
  ): void {
    if (this.scaffoldPending) {
      // Already scheduled — upgrade entityRef if we now have one.
      if (entityRef && !this.scaffoldPending.entityRef) {
        this.scaffoldPending.entityRef = entityRef;
      }
      return;
    }

    this.scaffoldPending = {
      timer: setTimeout(async () => {
        const pending = this.scaffoldPending;
        this.scaffoldPending = null;
        if (!pending) return;

        // If no entityRef yet (create event didn't fire), try resolving it from
        // the Scaffolder API using the task ID embedded in the navigate URL.
        let resolvedEntityRef = pending.entityRef;
        if (!resolvedEntityRef) {
          const match = SCAFFOLDER_TASK_RE.exec(pending.event.subject);
          if (match) {
            resolvedEntityRef = await this.fetchTaskEntityRef(match[1]);
          }
        }

        this.postEvent('scaffold_create', pending.event, resolvedEntityRef).catch(
          () => {},
        );
      }, 1000),
      entityRef,
      event,
    };
  }

  /**
   * Fetches the template entityRef for a scaffolder task from the Scaffolder API.
   * Returns null if the task cannot be found or the API call fails.
   */
  private async fetchTaskEntityRef(taskId: string): Promise<string | null> {
    try {
      const base = await this.discoveryApi.getBaseUrl('scaffolder');
      const resp = await this.fetchApi.fetch(`${base}/v2/tasks/${taskId}`);
      if (!resp.ok) return null;
      const data = (await resp.json()) as {
        spec?: { templateInfo?: { entityRef?: string } };
      };
      return data.spec?.templateInfo?.entityRef ?? null;
    } catch {
      return null;
    }
  }

  private async postEvent(
    namespacedAction: string,
    event: AnalyticsEvent,
    entityRefOverride?: string | null,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('custom-metrics');

    const entityRef =
      entityRefOverride ?? (event.context.entityRef as string | undefined);

    const body = JSON.stringify({
      action: namespacedAction,
      subject: event.subject,
      entityRef,
      timestamp: new Date().toISOString(),
    });

    const response = await this.fetchApi.fetch(`${baseUrl}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (!response.ok) {
      // Throw so the `.catch()` in captureEvent can swallow it.
      throw new Error(`POST /events failed with status ${response.status}`);
    }
  }
}
